import { useState, useEffect } from 'react'
import { Formik } from 'formik'
import AuthService from 'services/auth'
import cutileiApi from 'services/cutileiApi'
import { getApiErrorMessage, logApiError } from 'services/apiError'
import NumberInput from 'components/Inputs/NumberInput'
import MoneyInput from 'components/Inputs/MoneyInput'
import Select from 'components/Inputs/Select'
import MainButton from 'components/Buttons/MainButton'
import billServiceValidator from 'validators/billServiceValidator'
import { FaTimes } from 'react-icons/fa'
import { DateTime } from 'luxon'

import {
  Form,
  FormField,
  CloseButton,
  FieldGrid,
  Row,
  InfoText,
  Label,
  WarningText,
  ErrorContainer
} from './styles'

export default function BillServiceForm ({
  bill,
  services = [],
  professionals = [],
  defaultProfessional = null,
  freeItems = [],
  onConfirm: handleConfirm,
  onClose: handleClose
}) {
  const [errorMessage, setErrorMessage] = useState (null)
  const [professionalServices, setProfessionalServices] = useState([])
  const token = AuthService.getToken ()
  const businessId = AuthService.getBusinessId ()

  const requestConfig = {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  }

  // A comissão do serviço no cadastro do salão é só o default: cada profissional
  // pode ter um percentual próprio para o mesmo serviço, cadastrado em
  // EditProfessional/Services. Esse é o valor que deve prevalecer aqui.
  const fetchProfessionalServices = async (professionalId) => {
    if (!professionalId) return []

    try {
      const { data } = await cutileiApi.get(
        `/businesses/${businessId}/professionals/${professionalId}/services?cutilei_services=false`,
        requestConfig
      )
      return data
    } catch (error) {
      logApiError(error)
      return []
    }
  }

  const getComissionPercentage = (service, services) =>
    services.find(s => s.id === service.id)?.comission_percentage ?? service.comission_percentage

  useEffect(() => {
    fetchProfessionalServices(defaultProfessional?.value).then(setProfessionalServices)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const handleServiceSelected = async (name, option, setFieldValue) => {
    const service = option.value
    const billDate = DateTime.fromISO (bill.date)
    const promotion = service.promotions.find (promotion => (
      promotion.weekdays.some (day => day.iso_number === billDate.weekday)
    ))

    let servicePrice = service.price

    if (!!promotion) {
      const promotionWeekday = promotion.weekdays.find (day => (
        day.iso_number === billDate.weekday
      ))

      if (promotionWeekday) {
        servicePrice = promotionWeekday.price
      }
    }

    setFieldValue ('custom_price', servicePrice)
    setFieldValue ('custom_comission_percentage', getComissionPercentage(service, professionalServices) * 100)
    setFieldValue (name, option)
  }

  const handleProfessionalSelected = async (name, option, setFieldValue, service) => {
    setFieldValue (name, option)

    const services = await fetchProfessionalServices(option?.value)
    setProfessionalServices(services)

    if (service) {
      setFieldValue ('custom_comission_percentage', getComissionPercentage(service.value, services) * 100)
    }
  }

  return (
    <Formik
      validationSchema={billServiceValidator}
      initialValues={{
        'service': null,
        'professional': defaultProfessional,
        'amount': 1,
        'custom_price': 0,
        'custom_comission_percentage': 0
      }}
      onSubmit={async (values, { setSubmitting }) => {
        try {
          const addedServices = []
          const professional_id = values.professional?.value || null
          const serviceData = {
            service_id: values.service.value.id,
            professional_id,
            amount: values.amount,
            custom_price: values.custom_price,
            custom_comission_percentage: values.custom_comission_percentage / 100
          }
  
          const handAndFootServices = services.filter (s => (
            s.name.toLowerCase () === 'mão' || s.name.toLowerCase () === 'pé'
          ))

          const hasHandOrFeetService = freeItems.some (service => (
            handAndFootServices.map (s => s.id).includes (service.id)
          ))

          if (values.service.value.name.toLowerCase () === 'pé e mão' && hasHandOrFeetService) {
            for (const service of handAndFootServices) {
              const { data: addedService } = await cutileiApi.post (
                `/business_bills/${bill.id}/services`, {
                  service_id: service.id,
                  professional_id,
                  amount: 1,
                  custom_price: service.price,
                  custom_comission_percentage: getComissionPercentage(service, professionalServices)
                }, requestConfig
              )
              addedServices.push (addedService)
            }
          } else {
            const { data: service } = await cutileiApi.post (
              `/business_bills/${bill.id}/services`, serviceData, requestConfig
            )
            addedServices.push (service)
          }
          
          setSubmitting (false)
          handleConfirm (addedServices)
          handleClose ()
        } catch (error) {
          setErrorMessage (getApiErrorMessage (error))
          setSubmitting (false)
          logApiError (error)
        }
      }}
    >
      {({
        values,
        errors,
        touched,
        isSubmitting,
        setFieldValue,
        setFieldTouched,
        handleBlur,
        handleSubmit
      }) => (
        <Form>
          <FieldGrid>
            <FormField>
              {touched.service && errors.service && (
                <WarningText>
                  {errors.service}
                </WarningText>
              )}
              <Select
                name='service'
                placeholder='Serviço...'
                value={values.service}
                options={services.map (service => ({
                  label: service.name, value: service
                }))}
                onChange={(name, option) => handleServiceSelected (name, option, setFieldValue)}
                onBlur={setFieldTouched}
                error={errors.service}
                touched={touched.service}
              />        
            </FormField>
            <FormField>
              <Select
                name='professional'
                placeholder='Profissional...'
                value={values.professional}
                options={professionals.map (professional => ({
                  label: professional.nickname, value: professional.id
                }))}
                onChange={(name, option) => handleProfessionalSelected (name, option, setFieldValue, values.service)}
                onBlur={setFieldTouched}
                error={errors.professional}
                touched={touched.professional}
              />
            </FormField>
            <FormField>
              <Label htmlFor='service_amount'>
                Qtd
              </Label>
              <NumberInput
                id='service_amount'
                name='amount'
                value={values.amount}
                minValue={1}
                maxValue={99}
                onChange={(name, value) => {
                  setFieldValue ('custom_price', (values.service?.value.price ?? 0) * value)
                  setFieldValue (name, value)
                }}
                style={{width: '100%'}}
              />
            </FormField>
            <FormField>
              <Label htmlFor='service_price'>
                Preço
              </Label>
              <MoneyInput
                id='service_price'
                name='custom_price'
                placeholder='Preço'
                value={values.custom_price}
                onValueChange={setFieldValue}
                onBlur={handleBlur ('custom_price')}
              />
            </FormField>
            <FormField>
              <Label htmlFor='service_comission_percentage'>
                Comissão (%)
              </Label>
              <NumberInput
                id='service_comission_percentage'
                name='custom_comission_percentage'
                value={values.custom_comission_percentage}
                minValue={0}
                maxValue={100}
                onChange={setFieldValue}
                style={{width: '100%'}}
              />
            </FormField>
            <MainButton
              label='Adicionar'
              onClick={handleSubmit}
              disabled={isSubmitting}
              loading={isSubmitting}
              width={110}
            />
            <CloseButton onClick={handleClose}>
              <FaTimes color='#FF3939' size={18}/>
            </CloseButton>
          </FieldGrid>
          <Row>
            {errorMessage && (
              <ErrorContainer>
                <InfoText>{errorMessage}</InfoText>
              </ErrorContainer>
            )}
          </Row>
        </Form>
      )}
    </Formik>
  )
}
